"use client";
import React, { useState } from "react";

type State = { status: "idle" | "loading" | "success" | "error"; message?: string };

const Newsletter: React.FC = () => {
  const [state, setState] = useState<State>({ status: "idle" });

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setState({ status: "loading" });

    const form = e.currentTarget;
    const email = (form.elements.namedItem("email") as HTMLInputElement)?.value?.trim();
    const botField = (form.elements.namedItem("company") as HTMLInputElement)?.value ?? "";

    if (botField) {
      setState({ status: "success", message: "Thanks!" });
      form.reset();
      return;
    }

    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        setState({
          status: "success",
          message:
            "Almost done! Check your inbox and confirm your subscription.",
        });
        form.reset();
      } else {
        setState({
          status: "error",
          message:
            data?.error ??
            "We couldn't subscribe this address. Please try again or contact support.",
        });
      }
    } catch {
      setState({
        status: "error",
        message: "Network error. Please try again.",
      });
    }
  }

  return (
    <div className="bg-white border-t py-12 sm:py-14 lg:py-15">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 lg:grid-cols-12 lg:gap-8 lg:px-8">
        <div className="max-w-xl text-xl font-bold tracking-tight text-[#002243] lg:col-span-7">
          <h2 className="inline sm:block lg:inline xl:block">
            Keep up to date with our tools, partners, and efforts.
          </h2>{" "}
          <p className="inline sm:block lg:inline xl:block">
            Sign up for our newsletter.
          </p>
        </div>

        <form className="w-full max-w-md lg:col-span-5 lg:pt-2" onSubmit={onSubmit} noValidate>
          {/* Honeypot */}
          <div className="hidden">
            <label htmlFor="company">Company</label>
            <input id="company" name="company" type="text" tabIndex={-1} autoComplete="off" />
          </div>

          <div className="flex gap-x-4">
            <label htmlFor="email-address" className="sr-only">
              Email address
            </label>
            <input
              id="email-address"
              name="email"
              type="email"
              required
              placeholder="Enter your email"
              autoComplete="email"
              className="min-w-0 flex-auto rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#002243] sm:text-sm sm:leading-6"
            />

            <button
              type="submit"
              disabled={state.status === "loading"}
              className="flex-none rounded bg-[#002243] px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#002243] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002243] disabled:opacity-60"
            >
              {state.status === "loading" ? "Subscribing..." : "Subscribe"}
            </button>
          </div>

          {state.status === "success" && (
            <p className="mt-4 text-sm leading-6 text-[#002243]">
              {state.message}
            </p>
          )}
          {state.status === "error" && (
            <p className="mt-4 text-sm leading-6 text-red-700">
              {state.message}
            </p>
          )}

          <p className="mt-4 text-sm leading-6 text-gray-900">
            We care about your data. Read our{" "}
            <a
              href="/privacy-policy"
              className="font-semibold text-[#002243] hover:text-[#002243]"
            >
              privacy&nbsp;policy
            </a>
            .
          </p>
        </form>
      </div>
    </div>
  );
};

export default Newsletter;
